/**
 * The module navigation list — a vertical set of icon+label links, scope-gated
 * via @solandia/core/permissions. Rendered inside the persistent desktop
 * sidebar AND the mobile drawer, so it takes a `variant` for styling and an
 * `onNavigate` callback (the drawer closes itself when a link is followed).
 */
import { NavLink } from 'react-router-dom';
import { useI18n } from '../i18n';
import { useFamily } from '../family/FamilyContext';
import { MODULES, isModuleVisibleInMode, type ModuleDef } from '../modules/registry';

export function ModuleNav({
  variant = 'sidebar',
  onNavigate,
}: {
  variant?: 'sidebar' | 'drawer';
  onNavigate?: () => void;
}) {
  const { t } = useI18n();
  const { membership, family } = useFamily();
  // Scope gate AND the current family-mode gate (co-parenting surfaces hide in
  // the default 'family' mode).
  const visible = MODULES.filter((m) =>
    isModuleVisibleInMode(m, membership, family.mode),
  );

  // A module's label override wins over its i18n key (new modules ship their
  // copy ahead of the dictionary).
  const labelOf = (mod: ModuleDef): string =>
    mod.label ?? (mod.labelKey ? t.nav[mod.labelKey] : mod.slug);

  return (
    <nav className={`module-nav module-nav-${variant}`} aria-label={t.nav.modules}>
      <ul className="module-nav-list">
        {visible.map((mod) => (
          <li key={mod.slug}>
            <NavLink
              to={`/m/${mod.slug}`}
              // The "Rodina" (sharing) item anchors the family tour step.
              data-tour={mod.slug === 'sharing' ? 'family' : undefined}
              className={({ isActive }) =>
                isActive ? 'module-nav-link active' : 'module-nav-link'
              }
              onClick={onNavigate}
            >
              <span className="module-nav-icon" aria-hidden="true">
                {mod.icon}
              </span>
              <span className="module-nav-label">{labelOf(mod)}</span>
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
